const safeString = (value, fallback = '') => {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const money = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

const quantity = (value, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

// Event geography describes the ticket, never the customer's address.
export const buildEventContext = (event = {}) => {
  const start = event?.dates?.[0]?.start;
  const date = start ? new Date(start) : null;
  return {
    ...(event?.brand?.name ? { event_brand: safeString(event.brand.name) } : {}),
    ...(date && Number.isFinite(date.getTime()) ? { event_date: date.toISOString() } : {}),
    ...(event?.venue?.city ? { event_city: safeString(event.venue.city) } : {}),
    ...(event?.venue?.name ? { event_venue: safeString(event.venue.name) } : {})
  };
};

export const resolveEventGroupId = (event, fallback = '') => safeString(
  event?._id || event?.id || event?.slug,
  fallback
);

export const resolveTicketTypeId = (ticket = {}) => safeString(
  ticket?.ticketTypeId || ticket?.ticketType?._id || ticket?._id || ticket?.id,
  ''
);

export const resolveTicketProductId = (event, ticket, fallback = '') => {
  const eventGroupId = resolveEventGroupId(event, fallback);
  const ticketTypeId = resolveTicketTypeId(ticket);
  return ticketTypeId ? `${eventGroupId}_${ticketTypeId}` : eventGroupId;
};

export const buildTicketProduct = ({ event, ticket, fallbackId = '', defaultQuantity = 1 } = {}) => {
  const id = resolveTicketProductId(event, ticket, fallbackId);
  const ticketName = safeString(ticket?.name || ticket?.ticketTypeName, 'Ticket');
  const eventName = safeString(event?.title, 'Event');
  return {
    id,
    content_name: `${eventName} - ${ticketName}`,
    quantity: quantity(ticket?.quantity, defaultQuantity),
    item_price: money(ticket?.unitPrice ?? ticket?.price)
  };
};

export const buildViewContentData = ({ event, lowestPrice = 0 } = {}) => {
  const eventGroupId = resolveEventGroupId(event, 'event');
  const eventName = safeString(event?.title, 'Event');
  return {
    ...buildEventContext(event),
    content_type: 'product_group',
    content_ids: [eventGroupId],
    contents: [{
      id: eventGroupId,
      content_name: eventName,
      quantity: 1,
      item_price: money(lowestPrice)
    }],
    content_name: eventName,
    content_category: safeString(event?.categories?.[0] || event?.category, 'event'),
    value: money(lowestPrice),
    currency: safeString(event?.currency, 'GBP').toUpperCase()
  };
};

export const buildTicketContentData = ({ event, items = [], value, currency = 'GBP', includeEventContext = false } = {}) => {
  const eventGroupId = resolveEventGroupId(event, 'event');
  const contents = (Array.isArray(items) ? items : [])
    .map((item) => buildTicketProduct({ event, ticket: item, fallbackId: eventGroupId }))
    .filter((item) => item.id && item.quantity > 0)
    .sort((left, right) => left.id.localeCompare(right.id));
  const numItems = contents.reduce((sum, item) => sum + item.quantity, 0);
  const resolvedValue = value === undefined
    ? contents.reduce((sum, item) => sum + (item.item_price * item.quantity), 0)
    : value;
  return {
    ...(includeEventContext ? buildEventContext(event) : {}),
    content_type: 'product',
    content_ids: contents.map((item) => item.id),
    contents,
    content_name: safeString(event?.title, 'Event'),
    content_category: safeString(event?.categories?.[0] || event?.category, 'event'),
    value: money(resolvedValue),
    currency: safeString(currency || event?.currency, 'GBP').toUpperCase(),
    num_items: numItems
  };
};
